import React, { useContext, useState, useEffect } from 'react';
import styled from 'styled-components';
import StatCard from '../components/StatCard';
import { ThemeContext } from '../contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';
import { authFetch } from '../services/authFetch';

// Add modal and button components (reuse from ProfilePage.js)
const ChatbotModalOverlay = styled.div`
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0,0,0,0.45);
  z-index: 3000;
  display: flex;
  align-items: center;
  justify-content: center;
`;
const ChatbotModalContent = styled.div`
  background: var(--background, #fff);
  border-radius: 18px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.25);
  width: 420px;
  max-width: 95vw;
  height: 600px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
  @media (max-width: 576px) {
    width: 98vw;
    height: 90vh;
    border-radius: 10px;
  }
`;
const ChatbotModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.25rem 0.5rem 1.25rem;
  background: var(--primary, #3354a5);
  color: #fff;
`;
const ChatbotModalTitle = styled.div`
  font-weight: 600;
  font-size: 1.1rem;
`;
const ChatbotModalClose = styled.button`
  background: none;
  border: none;
  color: #fff;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0 0.5rem;
  line-height: 1;
`;
const ChatbotIframe = styled.iframe`
  flex: 1;
  border: none;
  width: 100%;
  min-height: 0;
  background: #f8f8f8;
`;
const ChatbotButton = styled.button`
  position: fixed;
  bottom: 32px;
  right: 32px;
  z-index: 2000;
  background: var(--primary, #3354a5);
  color: white;
  border: none;
  border-radius: 50%;
  width: 64px;
  height: 64px;
  box-shadow: 0 4px 16px rgba(51, 84, 165, 0.18);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s, box-shadow 0.2s, transform 0.2s;
  font-size: 2rem;
  padding: 0;
  overflow: hidden;
  &:hover {
    background: #2a4580;
    box-shadow: 0 8px 24px rgba(51, 84, 165, 0.25);
    transform: translateY(-2px) scale(1.05);
  }
  @media (max-width: 576px) {
    width: 48px;
    height: 48px;
    bottom: 16px;
    right: 16px;
    font-size: 1.5rem;
  }
`;

const WhatsAppButton = styled.button`
  position: fixed;
  bottom: 112px;
  right: 32px;
  z-index: 2000;
  background: #25D366;
  color: white;
  border: none;
  border-radius: 50%;
  width: 64px;
  height: 64px;
  box-shadow: 0 4px 16px rgba(37, 211, 102, 0.18);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s, box-shadow 0.2s, transform 0.2s;
  font-size: 2rem;
  padding: 0;
  overflow: hidden;
  &:hover {
    background: #20B954;
    box-shadow: 0 8px 24px rgba(37, 211, 102, 0.25);
    transform: translateY(-2px) scale(1.05);
  }
  @media (max-width: 576px) {
    width: 48px;
    height: 48px;
    bottom: 80px;
    right: 16px;
    font-size: 1.5rem;
  }
`;

const WhatsAppLabel = styled.div`
  position: fixed;
  bottom: 120px;
  right: 108px;
  z-index: 2000;
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 0.5rem 0.75rem;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 500;
  white-space: nowrap;
  pointer-events: none;
  opacity: 0;
  transform: translateX(10px);
  transition: opacity 0.2s, transform 0.2s;
  ${WhatsAppButton}:hover + & {
    opacity: 1;
    transform: translateX(0);
  }
  @media (max-width: 576px) {
    bottom: 88px;
    right: 72px;
    font-size: 0.75rem;
    padding: 0.375rem 0.5rem;
  }
`;
const ChatIcon = ({ size = 38 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}>
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);

const WhatsAppIcon = ({ size = 38 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" style={{ display: 'block' }}>
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.465 3.516z" />
  </svg>
);

const TeacherHomePage = () => {
  const navigate = useNavigate();
  const { darkMode } = useContext(ThemeContext);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Teacher data state with dynamic parts
  const [teacherData, setTeacherData] = useState({
    name: 'Loading...',
    position: '',
    classType: '',
    teachingSubject: '',
    headOfClass: null,
    avatarUrl: 'https://i.pravatar.cc/300?img=12',
    stats: [
      {
        label: 'Lapor KTS',
        icon: '⚠️',
        bgColor: '#ffe6e6', // soft red
        description: 'Laporkan ketidaksesuaian perilaku siswa'
      },
      {
        label: 'Status Siswa',
        icon: '👁️',
        bgColor: '#e8f1fd', // soft blue
        description: 'Lihat status dan perkembangan siswa'
      },
      {
        label: 'Rekap Laporan',
        icon: '📊',
        bgColor: '#e6f8f5', // soft green
        description: 'Lihat rekapitulasi laporan ketidaksesuaian'
      },
      {
        label: 'Konseling',
        icon: '🤝',
        bgColor: '#fff3e0', // soft yellow
        description: 'Layanan konseling dan bimbingan siswa'
      }
    ],
    recentReports: [] // Will be populated from API
  });

  // State for reports loading and error
  const [reportsLoading, setReportsLoading] = useState(true);
  const [reportsError, setReportsError] = useState('');

  // Add at the top, after other imports
  // Add modal and button components (reuse from ProfilePage.js)
  const ChatbotModalOverlay = styled.div`
    position: fixed;
    top: 0; left: 0; right: 0; bottom: 0;
    background: rgba(0,0,0,0.45);
    z-index: 3000;
    display: flex;
    align-items: center;
    justify-content: center;
  `;
  const ChatbotModalContent = styled.div`
    background: var(--background, #fff);
    border-radius: 18px;
    box-shadow: 0 8px 32px rgba(0,0,0,0.25);
    width: 420px;
    max-width: 95vw;
    height: 600px;
    max-height: 90vh;
    display: flex;
    flex-direction: column;
    position: relative;
    overflow: hidden;
    @media (max-width: 576px) {
      width: 98vw;
      height: 90vh;
      border-radius: 10px;
    }
  `;
  const ChatbotModalHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1rem 1.25rem 0.5rem 1.25rem;
    background: var(--primary, #3354a5);
    color: #fff;
  `;
  const ChatbotModalTitle = styled.div`
    font-weight: 600;
    font-size: 1.1rem;
  `;
  const ChatbotModalClose = styled.button`
    background: none;
    border: none;
    color: #fff;
    font-size: 1.5rem;
    cursor: pointer;
    padding: 0 0.5rem;
    line-height: 1;
  `;
  const ChatbotIframe = styled.iframe`
    flex: 1;
    border: none;
    width: 100%;
    min-height: 0;
    background: #f8f8f8;
  `;
  const ChatbotButton = styled.button`
    position: fixed;
    bottom: 32px;
    right: 32px;
    z-index: 2000;
    background: var(--primary, #3354a5);
    color: white;
    border: none;
    border-radius: 50%;
    width: 64px;
    height: 64px;
    box-shadow: 0 4px 16px rgba(51, 84, 165, 0.18);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: background 0.2s, box-shadow 0.2s, transform 0.2s;
    font-size: 2rem;
    padding: 0;
    overflow: hidden;
    &:hover {
      background: #2a4580;
      box-shadow: 0 8px 24px rgba(51, 84, 165, 0.25);
      transform: translateY(-2px) scale(1.05);
    }
    @media (max-width: 576px) {
      width: 48px;
      height: 48px;
      bottom: 16px;
      right: 16px;
      font-size: 1.5rem;
    }
  `;
  const ChatIcon = ({ size = 38 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}>
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  );

  // Add the button and modal at the end of the main return (inside PageContainer, after all content):
  const [showChatbot, setShowChatbot] = useState(false);

  // Fetch teacher data from API
  useEffect(() => {
    const fetchTeacherData = async () => {
      try {
        // Get token from localStorage
        const token = localStorage.getItem('token');

        if (!token) {
          setError('Authentication token not found. Please login again.');
          setLoading(false);
          return;
        }

        const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/';
        const response = await fetch(`${API_BASE_URL.replace(/\/$/, '')}/api/teachers/me`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch teacher data');
        }

        const teacherApiData = await response.json();

        // Dynamically build stats array to control order
        const stats = [
          {
            label: 'Lapor KTS',
            path: 'lapor-kts',
            icon: '⚠️',
            bgColor: '#ffe6e6', // soft red
            description: 'Laporkan ketidaksesuaian perilaku siswa'
          },
        ];

        // Add 'Laporan Lembur' second if the teacher is a 'praktek' teacher
        const classTypes = Array.isArray(teacherApiData.class_type) ? teacherApiData.class_type : (teacherApiData.class_type ? [teacherApiData.class_type] : []);
        if (classTypes.includes('praktek')) {
          stats.push(
            {
              label: 'Lapor Lembur',
              path: 'lapor-lembur',
              icon: '📝',
              bgColor: '#e8f5e8', // soft green
              description: 'Buat laporan lembur untuk guru'
            },
            {
              label: 'Rekap Lembur',
              path: 'laporan-lembur',
              icon: '⏰',
              bgColor: '#f3e8fd', // soft purple
              description: 'Kelola rekap laporan lembur siswa bengkel'
            },
            // {
            //   label: 'Ijin Interupsi',
            //   path: 'ijin-interupsi',
            //   icon: '🚪',
            //   bgColor: '#fff8e1', // soft yellow
            //   description: 'Ajukan permohonan ijin interupsi'
            // }
          );
        }

        // Add the rest of the default cards
        stats.push(
          {
            label: 'Status Siswa',
            path: 'status-siswa',
            icon: '👁️',
            bgColor: '#e8f1fd', // soft blue
            description: 'Lihat status dan perkembangan siswa'
          },
          {
            label: 'Rekap Laporan',
            path: 'rekap-laporan',
            icon: '📊',
            bgColor: '#e6f8f5', // soft green
            description: 'Lihat rekapitulasi laporan ketidaksesuaian'
          }
        );

        // Add other conditional cards at the end
        if (classTypes.includes('bk')) {
          stats.push({
            label: 'Konseling',
            path: 'konseling',
            icon: '🤝',
            bgColor: '#fff3e0', // soft yellow
            description: 'Layanan konseling dan bimbingan siswa'
          });
        }

        if (teacherApiData.headOfClass) {
          stats.push({
            label: 'Wali Kelas Monitoring',
            path: 'wali-kelas-monitoring',
            icon: '📈',
            bgColor: '#f3e8fd', // soft purple
            description: 'Monitor statistik kelas Anda'
          });
        }

        setTeacherData(prevData => {
          return {
            ...prevData,
            name: teacherApiData.name || 'Unknown Teacher',
            position: teacherApiData.headOfClass ? `Wali Kelas ${teacherApiData.headOfClass}` : 'Guru',
            classType: classTypes.length > 0 ? classTypes.map(type => type.charAt(0).toUpperCase() + type.slice(1)).join(', ') : '',
            teachingSubject: teacherApiData.teachingSubject || '',
            avatarUrl: teacherApiData.avatarUrl
              ? (teacherApiData.avatarUrl.startsWith('http')
                ? teacherApiData.avatarUrl
                : `${process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/'}${teacherApiData.avatarUrl.startsWith('/') ? teacherApiData.avatarUrl : `/${teacherApiData.avatarUrl}`}`)
              : '/images/user.png',
            stats: stats,
            headOfClass: teacherApiData.headOfClass
          };
        });

        setLoading(false);
      } catch (err) {
        console.error('Error fetching teacher data:', err);
        setError('Failed to load teacher data. Please refresh the page.');
        setLoading(false);
      }
    };

    fetchTeacherData();
  }, []);

  // Fetch latest reports from API
  useEffect(() => {
    const fetchLatestReports = async () => {
      try {
        // Get token from localStorage
        const token = localStorage.getItem('token');

        if (!token) {
          setReportsError('Authentication token not found. Please login again.');
          setReportsLoading(false);
          return;
        }

        const response = await authFetch('/api/teacher/latest-reports', {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch latest reports');
        }

        const result = await response.json();

        if (result.success && result.data) {
          // Update state with fetched reports
          setTeacherData(prevData => ({
            ...prevData,
            recentReports: result.data
          }));
        } else {
          throw new Error('Invalid response format');
        }

        setReportsLoading(false);
      } catch (err) {
        console.error('Error fetching latest reports:', err);
        setReportsError('Failed to load latest reports. Please refresh the page.');
        setReportsLoading(false);
      }
    };

    fetchLatestReports();
  }, []);

  // Helper function to map KTS status from database to frontend
  const mapStatusToFrontend = (dbStatus) => {
    switch (dbStatus) {
      case 'disetujui':
        return 'approved';
      case 'ditolak':
        return 'rejected';
      case 'menunggu':
        return 'pending';
      case 'dibatalkan':
        return 'cancelled';
      case 'approved':
        return 'approved';
      case 'rejected':
        return 'rejected';
      case 'pending':
        return 'pending';
      default:
        return 'pending';
    }
  };

  // Helper function to get status text
  const getStatusText = (status) => {
    switch (status) {
      case 'pending':
        return 'menunggu';
      case 'approved':
        return 'disetujui';
      case 'rejected':
        return 'ditolak';
      case 'cancelled':
        return 'dibatalkan';
      default:
        return 'menunggu';
    }
  };

  // Handle clicking on a card
  const handleCardClick = (stat) => {
    console.log(`Clicked on ${stat.label} card`);
    const path = `/teacher/${stat.path}`;
    navigate(path);
  };


  return (
    <PageContainer>
      <PageHeader>
        <h1>Dashboard Guru</h1>
      </PageHeader>

      {loading ? (
        <LoadingContainer>
          <LoadingText>Memuat data guru...</LoadingText>
        </LoadingContainer>
      ) : error ? (
        <ErrorContainer>
          <ErrorText>{error}</ErrorText>
          <RefreshButton onClick={() => window.location.reload()}>
            Muat Ulang
          </RefreshButton>
        </ErrorContainer>
      ) : (
        <ProfileCard>
          <ProfileHeader>
            <AvatarContainer>
              <Avatar src={teacherData.avatarUrl} alt={teacherData.name} />
            </AvatarContainer>
            <ProfileInfo>
              <h2>{teacherData.name}</h2>
              <InfoRow>
                <InfoItem>{teacherData.position}</InfoItem>
                {teacherData.classType && (
                  <>
                    <InfoDivider>|</InfoDivider>
                    <InfoItem>Kelas: {teacherData.classType}</InfoItem>
                  </>
                )}
                {teacherData.teachingSubject && (
                  <>
                    <InfoDivider>|</InfoDivider>
                    <InfoItem>Mata Pelajaran: {teacherData.teachingSubject}</InfoItem>
                  </>
                )}
              </InfoRow>
            </ProfileInfo>
          </ProfileHeader>
        </ProfileCard>
      )}

      <MainContent>
        <DashboardSection>
          <TeacherStatGrid>
            {teacherData.stats.map((stat, index) => (
              <TeacherStatCard
                key={index}
                onClick={() => handleCardClick(stat)}
              >
                <StatIconWrapper style={{ backgroundColor: darkMode ? '#333' : stat.bgColor }}>
                  <StatIcon>{stat.icon}</StatIcon>
                </StatIconWrapper>
                <StatCardTitle>{stat.label}</StatCardTitle>
                <StatCardDescription>{stat.description}</StatCardDescription>
              </TeacherStatCard>
            ))}
          </TeacherStatGrid>
        </DashboardSection>

        <DashboardSection>
          <SectionTitle>Laporan Terkini</SectionTitle>

          {reportsLoading ? (
            <LoadingContainer>
              <LoadingText>Memuat laporan terkini...</LoadingText>
            </LoadingContainer>
          ) : reportsError ? (
            <ErrorContainer>
              <ErrorText>{reportsError}</ErrorText>
              <RefreshButton onClick={() => window.location.reload()}>
                Muat Ulang
              </RefreshButton>
            </ErrorContainer>
          ) : teacherData.recentReports.length === 0 ? (
            <EmptyStateContainer>
              <EmptyStateText>Belum ada laporan terkini</EmptyStateText>
            </EmptyStateContainer>
          ) : (
            <ReportsList>
              {teacherData.recentReports.map((report) => {
                const mappedStatus = mapStatusToFrontend(report.status);
                const statusText = getStatusText(mappedStatus);

                return (
                  <ReportItem key={report.id}>
                    <ReportIcon type={report.type}>
                      {report.type === 'kts' ? '⚠️' : (report.type === 'overtime' ? '⏰' : '🤝')}
                    </ReportIcon>
                    <ReportContent>
                      <ReportTitle>{report.title}</ReportTitle>
                      <ReportDescription>{report.description}</ReportDescription>
                      <ReportMeta>
                        <span>Siswa: {report.student}</span>
                        <span>Tanggal: {formatDate(report.date)}</span>
                      </ReportMeta>
                    </ReportContent>
                    <ReportStatusBadge status={mappedStatus}>
                      {statusText}
                    </ReportStatusBadge>
                  </ReportItem>
                );
              })}
            </ReportsList>
          )}
        </DashboardSection>
      </MainContent>
      <WhatsAppButton
        aria-label="WhatsApp Pamong"
        title="Hubungi WA Pamong"
        onClick={() => window.open('https://api.whatsapp.com/send/?phone=6281548011463&text&type=phone_number&app_absent=0', '_blank')}
      >
        <WhatsAppIcon size={window.innerWidth <= 576 ? 28 : 38} />
      </WhatsAppButton>
      <WhatsAppLabel>WA Pamong</WhatsAppLabel>
      <ChatbotButton
        aria-label="Chatbot"
        title="Buka Chatbot"
        onClick={() => setShowChatbot(true)}
      >
        <ChatIcon size={window.innerWidth <= 576 ? 28 : 38} />
      </ChatbotButton>
      {showChatbot && (
        <ChatbotModalOverlay onClick={() => setShowChatbot(false)}>
          <ChatbotModalContent onClick={e => e.stopPropagation()}>
            <ChatbotModalHeader>
              <ChatbotModalTitle>Chatbot Bantuan</ChatbotModalTitle>
              <ChatbotModalClose onClick={() => setShowChatbot(false)}>&times;</ChatbotModalClose>
            </ChatbotModalHeader>
            <ChatbotIframe
              src="https://automation.canvaswebdesign.com/webhook/6ea2ebe9-ae12-4df0-ab4b-ee13e9cecdba/chat"
              title="Chatbot"
              allow="microphone; camera"
            />
          </ChatbotModalContent>
        </ChatbotModalOverlay>
      )}
    </PageContainer>
  );
};

// Helper function to format dates
const formatDate = (dateString) => {
  const options = { year: 'numeric', month: 'short', day: 'numeric' };
  return new Date(dateString).toLocaleDateString('id-ID', options);
};

const PageContainer = styled.div`
  padding: 1.5rem;
  max-width: 1280px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    padding: 1rem 0.5rem;
  }
`;

const PageHeader = styled.div`
  margin-bottom: 1.5rem;
  
  h1 {
    font-size: 1.75rem;
    font-weight: 600;
    color: var(--text-dark);
  }
`;

const ProfileCard = styled.div`
  background-color: var(--background);
  border-radius: 16px;
  overflow: hidden;
  box-shadow: var(--box-shadow);
  margin-bottom: 1.5rem;
  transition: box-shadow 0.3s ease, background-color 0.3s;
  
  &:hover {
    box-shadow: 0 12px 28px rgba(0, 0, 0, 0.15);
  }
`;

const ProfileHeader = styled.div`
  padding: 1.5rem;
  display: flex;
  align-items: center;
  gap: 1.5rem;
  
  @media (max-width: 576px) {
    flex-direction: row;
    align-items: center; /* Vertically align items */
    text-align: left;
    padding: 1rem;
    gap: 1rem;
  }
`;

const AvatarContainer = styled.div`
  flex-shrink: 0;
`;

const Avatar = styled.img`
  width: 100px; /* Reduced from 120px */
  height: 100px; /* Reduced from 120px */
  border-radius: 50%;
  object-fit: cover;
  border: 3px solid var(--primary);
  
  @media (max-width: 576px) {
    width: 60px;
    height: 60px;
    border-width: 2px;
  }
`;

const ProfileInfo = styled.div`
  h2 {
    font-size: 1.5rem;
    font-weight: 600;
    margin: 0 0 0.5rem 0; /* Adjusted margin */
  }
  
  @media (max-width: 576px) {
    h2 {
      font-size: 1.2rem; /* Adjusted for mobile */
      margin-bottom: 0.25rem;
    }
  }
`;

const InfoRow = styled.div`
  display: flex;
  flex-direction: column; /* Stack items vertically */
  align-items: flex-start; /* Align items to the left */
  gap: 5px; /* Reduced gap */
  margin-top: 5px;
  
  @media (max-width: 576px) {
    gap: 2px; /* Further reduced gap for smaller screens */
  }
`;

const InfoItem = styled.div`
  color: var(--text-dark);
  font-size: 1rem; /* Adjusted font size */
  font-weight: 500;
  
  @media (max-width: 576px) {
    font-size: 0.875rem; /* Adjusted for mobile */
    margin-bottom: 0; /* Removed bottom margin */
    text-align: left; /* Ensure text is left-aligned */
  }
`;

const InfoDivider = styled.span`
  display: none; /* Hide divider */
`;

const MainContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const DashboardSection = styled.section`
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--text-dark);
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid var(--border);
`;

const TeacherStatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1.5rem;
  
  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 576px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 0.75rem;
    padding: 0.5rem;
  }
`;

const TeacherStatCard = styled.div`
  background-color: var(--background);
  border-radius: 16px;
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  box-shadow: var(--box-shadow);
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s, background-color 0.3s;
  height: 100%;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
  }

  @media (max-width: 576px) {
    padding: 1rem;
    border-radius: 12px;
    min-height: 140px;
    
    &:hover {
      transform: translateY(-2px);
    }
  }
`;

const StatIconWrapper = styled.div`
  width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  margin-bottom: 1rem;
  
  @media (max-width: 576px) {
    width: 50px;
    height: 50px;
    margin-bottom: 0.5rem;
  }
`;

const StatIcon = styled.span`
  font-size: 2.5rem;
  
  @media (max-width: 576px) {
    font-size: 1.75rem;
  }
`;

const StatCardTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: var(--text-dark);
  
  @media (max-width: 576px) {
    font-size: 1rem;
    margin-bottom: 0.25rem;
  }
`;

const StatCardValue = styled.div`
  font-size: 1rem;
  color: var(--primary);
  font-weight: 600;
  margin-bottom: 0.75rem;
`;

const StatCardDescription = styled.p`
  font-size: 0.875rem;
  color: var(--text-light);
  line-height: 1.4;
  
  @media (max-width: 576px) {
    font-size: 0.75rem;
    line-height: 1.3;
  }
`;

const ReportsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const ReportItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  background-color: var(--background);
  border-radius: 12px;
  padding: 1.25rem;
  box-shadow: var(--box-shadow);
  transition: transform 0.2s, box-shadow 0.2s;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.1);
  }
  
  @media (max-width: 576px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }
`;

const ReportIcon = styled.div`
  width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  background-color: ${props => {
    const isDark = document.documentElement.getAttribute('data-theme') === 'dark';
    if (isDark) {
      if (props.type === 'kts') return '#3a2a2a'; // dark red
      if (props.type === 'overtime') return '#3a3020'; // dark yellow
      return '#2c3b56'; // dark blue for konseling
    } else {
      if (props.type === 'kts') return '#ffe6e6'; // light red
      if (props.type === 'overtime') return '#f3e8fd'; // soft purple
      return '#e8f1fd'; // light blue for konseling
    }
  }};
  font-size: 1.5rem;
  flex-shrink: 0;
  
  @media (max-width: 576px) {
    width: 40px;
    height: 40px;
    font-size: 1.25rem;
  }
`;

const ReportContent = styled.div`
  flex: 1;
`;

const ReportTitle = styled.h4`
  font-size: 1.125rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: var(--text-dark);
`;

const ReportDescription = styled.p`
  font-size: 0.9375rem;
  color: var(--text-dark);
  margin-bottom: 0.75rem;
  line-height: 1.4;
`;

const ReportMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  font-size: 0.875rem;
  color: var(--text-light);
  
  @media (max-width: 576px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.25rem;
  }
`;

const ReportStatusBadge = styled.div`
  padding: 0.5rem 1rem;
  border-radius: 50px;
  font-size: 0.813rem;
  font-weight: 600;
  background-color: ${props => {
    const isDark = document.documentElement.getAttribute('data-theme') === 'dark';
    if (isDark) {
      switch (props.status) {
        case 'pending': return '#3a3020'; // dark yellow
        case 'approved': return '#1e3a32'; // dark green
        case 'rejected': return '#3a2a2a'; // dark red
        default: return '#333333';
      }
    } else {
      switch (props.status) {
        case 'pending': return '#fff3e0'; // light yellow
        case 'approved': return '#e6f8f5'; // light green
        case 'rejected': return '#ffe6e6'; // light red
        default: return '#f5f5f5';
      }
    }
  }};
  color: ${props => {
    switch (props.status) {
      case 'pending': return '#e67700'; // yellow
      case 'approved': return '#2ec27e'; // green
      case 'rejected': return '#c01c28'; // red
      default: return 'var(--text-dark)';
    }
  }};
  white-space: nowrap;
  flex-shrink: 0;
  
  @media (max-width: 576px) {
    align-self: flex-start;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: var(--background);
  border-radius: 16px;
  padding: 2rem;
  box-shadow: var(--box-shadow);
  margin-bottom: 1.5rem;
`;

const LoadingText = styled.p`
  font-size: 1.125rem;
  color: var(--text-dark);
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background-color: var(--background);
  border-radius: 16px;
  padding: 2rem;
  box-shadow: var(--box-shadow);
  margin-bottom: 1.5rem;
  border-left: 4px solid #c01c28;
`;

const ErrorText = styled.p`
  font-size: 1.125rem;
  color: var(--text-dark);
  margin-bottom: 1rem;
`;

const RefreshButton = styled.button`
  background-color: var(--primary);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.75rem 1.5rem;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: var(--primary-dark);
  }
`;

const EmptyStateContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: var(--background);
  border-radius: 16px;
  padding: 2rem;
  box-shadow: var(--box-shadow);
  margin-bottom: 1.5rem;
`;

const EmptyStateText = styled.p`
  font-size: 1.125rem;
  color: var(--text-light);
  font-style: italic;
`;


export default TeacherHomePage;